/**
 * Telling the listener's voice from the narrator's own.
 *
 * While the article is read, the microphone hears the speakers too. Echo
 * cancellation keeps most of it out, but on a laptop the recognizer still
 * returns fragments of the narration now and then, and acting on them would
 * make the reader interrupt itself. The guard is a word overlap: what was
 * heard counts as echo when most of its words were just spoken.
 */

const CJK_RE = /[぀-ヿ㐀-鿿가-힯]/;

/** Lowercase words without accents or punctuation. */
export function speechWords(text: string | null | undefined): string[] {
  if (!text) return [];
  return text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

/**
 * True when `heard` is (mostly) the narrator's own words coming back through
 * the microphone. `spoken` is the text of the last few utterances.
 */
export function looksLikeEcho(heard: string, spoken: string, threshold: number = 0.6): boolean {
  const h = speechWords(heard);
  if (h.length === 0) return true;
  const s = new Set(speechWords(spoken));
  if (s.size === 0) return false;
  // Japanese recognition returns no spaces: compare as substrings instead.
  if (h.length === 1 && CJK_RE.test(h[0])) {
    const flat = Array.from(s).join('');
    return h[0].length >= 2 && flat.includes(h[0]);
  }
  let hits = 0;
  for (const w of h) if (s.has(w)) hits++;
  return hits / h.length >= threshold;
}

/**
 * Enough speech to count as the listener talking: two words, or one run of
 * at least four characters in a script written without spaces. A cough or a
 * stray "the" never interrupts the reading.
 */
export function isMeaningfulSpeech(heard: string): boolean {
  const w = speechWords(heard);
  if (w.length >= 2) return true;
  return w.length === 1 && CJK_RE.test(w[0]) && w[0].length >= 4;
}
